import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { useAuth } from 'firebaseHooks/AuthContext';
import Button from '@mui/material/Button';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { ssoSignOut } from 'sso/ssoClient';
import ExitToApp from '@mui/icons-material/ExitToApp';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import {
  Link as MUILink, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import useGlobal from 'global-state/store';
import { useState } from 'react';
import LockResetIcon from '@mui/icons-material/LockReset';
import LanguageSelect from './LanguageSelect';

export default function MyAccount(props) {
  const { t } = useTranslation();
  // eslint-disable-next-line no-unused-vars
  const { currentUser, initializing } = useAuth();
  const auth = getAuth();
  const navigate = useNavigate();
  const [, globalActions] = useGlobal();
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');

  function email() {
    if (currentUser) {
      return currentUser.email;
    }
    return '_';
  }

  // Check if user is signed in with Google
  const isGoogleUser = currentUser?.providerData?.some((provider) => provider.providerId === 'google.com');

  const logout = () => {
    ssoSignOut(auth).then(() => {
      globalActions.setUserStatus('initial');
      navigate('/');
    });
  };

  const handleResetPassword = async () => {
    if (!resetEmail) {
      setResetMessage('Please enter an email address');
      return;
    }

    setResetLoading(true);
    setResetMessage('');

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage('Password reset email sent! Please check your inbox.');
      setResetEmail('');
    } catch (error) {
      setResetMessage(`Error: ${error.message}`);
    } finally {
      setResetLoading(false);
    }
  };

  const openResetDialog = () => {
    setResetEmail(currentUser?.email || '');
    setResetMessage('');
    setResetDialogOpen(true);
  };

  const closeResetDialog = () => {
    setResetDialogOpen(false);
    setResetEmail('');
    setResetMessage('');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        width: '100%',
        maxWidth: 600,
        mx: 'auto',
        px: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 3,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
        }}
      >

        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          gap: 2,
          mb: 2,
        }}
        >
          <Typography component="span" sx={{ fontWeight: 'bold' }} variant="h6">{email()}</Typography>

          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            width: '100%',
          }}
          >
            {!isGoogleUser && (
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={openResetDialog}
                startIcon={<LockResetIcon />}
              >
                Reset Password
              </Button>
            )}

            <Button
              variant="outlined"
              color="inherit"
              sx={{ color: 'red' }}
              size="large"
              onClick={logout}
              startIcon={<ExitToApp />}
            >
              {t('user_card.quit')}
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography>{t('welcome.contact_message')}</Typography>
          <MUILink
            component={Link}
            to="/contact"
            color="primary"
            sx={{ fontWeight: 'bold' }}
          >
            contact@labox-apps.com
          </MUILink>
        </Box>
      </Paper>
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        width: '100%',
        gap: 1,
      }}
      >
        <LanguageSelect />
      </Box>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onClose={closeResetDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            sx={{ mt: 2 }}
          />
          {resetMessage && (
            <Alert
              severity={resetMessage.includes('Error') ? 'error' : 'success'}
              sx={{ mt: 2 }}
            >
              {resetMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResetDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleResetPassword}
            color="primary"
            variant="contained"
            disabled={resetLoading || !resetEmail}
          >
            {resetLoading ? 'Sending...' : 'Send Reset Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
